import * as PIXI from "pixi.js";
import FXManager from "./FXManager";
import { Settings } from "../../settings";
import { soundManager } from "../../SoundManager";
import { SOUND_NAME } from "../../sounds";

// ── Per-frame sound dedup ────────────────────────────────────────────────────
// In super turbo all bolts fire/impact in the same frame, stacking 10× sounds.
// This Set tracks which sounds already played this frame; a single rAF clears it.
// Same proven pattern as useLandingAnimations.js _playOncePerFrame.
const _frameSoundsPlayed = new Set();
let _frameSoundRAF = null;

function _playOncePerFrame(soundName) {
  if (_frameSoundsPlayed.has(soundName)) return;
  _frameSoundsPlayed.add(soundName);
  soundManager.play(soundName);
  if (!_frameSoundRAF) {
    _frameSoundRAF = requestAnimationFrame(() => {
      _frameSoundsPlayed.clear();
      _frameSoundRAF = null;
    });
  }
}

// ─── Lightning Bolt Config ────────────────────────────────────────────────────
// Single source of truth for ALL Zeus ↔ Pot lightning bolt animations.
// Shared by PotChargingAnimator (Zeus→Pot) and PotCollectionOrbActivator (Pot→Coins/Zeus).
// Edit here to adjust feel — no need to touch animation logic below.

export const BOLT_CONFIG = {
  // Phase timings (ms)
  CHARGE_MS: 120,          // buildup before bolt shoots
  IMPACT_MS: 120,           // shrink-into-target after arrival

  // Slingshot pullback (fraction of bolt length pulled back during charge)
  PULLBACK_FRACTION: 0.5,

  // Bolt length minimums (× symbolHeight).
  // Top row (closest to pot) gets a shorter bolt; all other rows get larger.
  TOP_ROW_BOLT_MULT: 1.5,
  OTHER_ROW_BOLT_MULT: 2.0,

  // Double-feature bolt multipliers by reel distance (source Zeus → target coin).
  // Closer targets get shorter bolts; far targets get the full dramatic bolt.
  DOUBLE_BOLT_MULT_CLOSE: 1.5,   // 0–1 reels apart (same column or adjacent)
  DOUBLE_BOLT_MULT_MID:   1.75,  // 2 reels apart
  DOUBLE_BOLT_MULT_FAR:   2.0,   // 3+ reels apart

  // Charge phase: bolt X-scale during buildup (controls width)
  CHARGE_SCALE_X: 2.0,
  // Charge phase: bolt grows to this fraction of full scaleY during charge
  CHARGE_GROW_FRACTION: 0.5,
  // Charge phase: bolt alpha at full charge
  CHARGE_ALPHA: 0.6,




  // Impact phase: bolt alpha fade (0 = stays full, 1 = fades to black)
  IMPACT_ALPHA_FADE: 0.7,

  // Energy balls (charge launch ball + impact ball)
  // Both ends use identical grow animation: starts at BALL_BASE_SCALE, grows by BALL_CHARGE_GROW.
  BALL_BASE_SCALE: 0.35,   // base scale relative to responsive scale

  // Ball grows by this extra amount during both charge and impact phases
  BALL_CHARGE_GROW: 0.25,

  // Minimum fraction of distance that must be travel (prevents bolt from covering
  // the full gap and appearing stationary). 0.35 = bolt uses at most 65% of distance,
  // leaving at least 35% for visible travel movement.
  MIN_TRAVEL_FRACTION: 0.35,
};

/**
 * Fire a slingshot lightning bolt from startPos → endPos.
 *
 * Shared animation engine for all Zeus ↔ Pot bolt effects.
 * Direction is irrelevant — pass any start/end, the effect is identical.
 *
 * 3-phase animation: charge (pullback) → travel → impact.
 * Positions are normalized at fire time and recomputed per-frame for resize resilience.
 *
 * @param {PIXI.Container} container - Container to render the bolt into
 * @param {{x: number, y: number}} startPos - Start position in container's local space
 * @param {{x: number, y: number}} endPos - End position in container's local space
 * @param {object} opts
 * @param {number} [opts.symbolIndex=1] - Target row (0=top/closest → shorter bolt, 1/2=other)
 * @param {number|null} [opts.boltMultOverride=null]
 *   When set, bypasses symbolIndex-based sizing and uses this multiplier directly.
 *   Use for bolts where distance is determined by a different axis (e.g. reel column distance).
 * @param {function(): {symbolWidth: number, symbolHeight: number}} opts.getDimensions
 *   Called each frame to read live dimensions (resize-resilient).
 * @param {number} opts.duration - Travel phase duration (ms)
 * @param {Array} opts.activeBolts - Caller's _activeBolts[] — entry is pushed/popped here
 * @param {function|null} [opts.onImpact] - Optional: called once at Phase 3 start
 * @param {number|null} [opts.widthFraction=null]
 *   Thickness as a fraction of the bolt's own LENGTH, instead of from the cell.
 *
 *   Zeus sizes width from `symbolHeight / Settings.SYMBOL_HEIGHT`, which is right
 *   for its own shot: a coin to a pot across the screen is ~800px, the bolt ~520,
 *   the art 74 wide — about 1:7, a bolt. Over a SHORT gap the length shrinks with
 *   the distance and that width does not, so a cell-to-neighbour strike came out
 *   104 long and 61 wide: a blob, not a bolt.
 *
 *   With this set, the width follows the length and the shape is the same at any
 *   distance and in any container scale. 0.14 is Zeus's own ratio.
 * @param {{emitter?:object,particle?:object,component?:object}|null} [opts.fxOverrides]
 *   Retune the `Lightning` emitter for THIS bolt only — density, strand life and
 *   MovieClip speed. The bundle is shared with Zeus, so this is how a game with
 *   a different cell size and a different gap gets a bolt that reads, without
 *   changing the one the artist authored. See fireCollectBolts.
 * @param {boolean} [opts.withBalls=true]
 *   The energy ball at each end — one charging at the source, one blooming at the
 *   target. Zeus fires with them; the Golden Mystery's collector asks for the
 *   BOLT ALONE, because a coin's value going into a magnet is a strike and not a
 *   launch. Everything else about the bolt is untouched, so the two read as the
 *   same effect with and without them.
 * @returns {Promise<void>} Resolves when animation completes
 */
export function fireLightningBolt(container, startPos, endPos, {
  symbolIndex = 1,
  boltMultOverride = null,
  widthFraction = null,
  getDimensions,
  duration,
  activeBolts,
  onImpact = null,
  withBalls = true,
  fxOverrides = null,
}) {
  if (!container || container.destroyed) {
    return Promise.resolve();
  }

  const { CHARGE_MS, IMPACT_MS, PULLBACK_FRACTION } = BOLT_CONFIG;
  const totalDuration = CHARGE_MS + duration + IMPACT_MS;

  let { symbolWidth, symbolHeight } = getDimensions();
  let responsiveScale = symbolHeight / Settings.SYMBOL_HEIGHT;

  // Normalize positions at fire time — recomputed per-frame for resize resilience
  const normStart = { x: startPos.x / symbolWidth, y: startPos.y / symbolHeight };
  const normEnd   = { x: endPos.x   / symbolWidth, y: endPos.y   / symbolHeight };

  let boltData = null;
  if (FXManager.isReady) {
    boltData = FXManager.emitLightning(container, startPos, endPos, fxOverrides);
  }

  // Native sprite height is constant after bundle load (bolts only fire well after
  // assets load) — read once instead of a PIXI.Assets.get() lookup every frame.
  const lightningNativeHeight = FXManager.lightningNativeHeight;

  let elapsed = 0;
  let chargeStarted = false;
  let impactStarted = false;

  let launchBall = null;
  let impactBall = null;
  const fadingBalls = []; // balls whose emitters are stopped but wrappers still need teardown

  // Hide during charge phase
  if (boltData?.wrapper) {
    boltData.wrapper.scale.set(responsiveScale, 0);
    boltData.wrapper.alpha = 0;
  }

  // Play lightning fly sound at bolt start (deduped — 1 per frame max)
  _playOncePerFrame(SOUND_NAME.lightning_fly);

  return new Promise((resolve) => {
    const boltEntry = {
      boltData, tickerFn: null, resolve,
      launchBall: null, impactBall: null,
    };

    // Teardown used by the early-abort paths (wrapper destroyed / degenerate
    // distance) — mirrors the DONE branch so an aborted bolt still splices its
    // entry and recycles its emitters instead of leaking them from the pool.
    const abortCleanup = () => {
      const idx = activeBolts.indexOf(boltEntry);
      if (idx !== -1) activeBolts.splice(idx, 1);
      FXManager.releaseOwnedFx(boltData);
      for (const ball of [...fadingBalls, launchBall, impactBall]) {
        FXManager.releaseOwnedFx(ball);
      }
    };

    boltEntry.tickerFn = () => {
      // Cap delta to prevent massive frame drops from skipping the entire animation.
      // Without this, a single 860ms frame would jump elapsed past totalDuration.
      const dt = Math.min(PIXI.Ticker.shared.deltaMS, 50);
      elapsed += dt;
      const wrapper = boltData?.wrapper;
      if (!wrapper || wrapper.destroyed) {
        PIXI.Ticker.shared.remove(boltEntry.tickerFn);
        abortCleanup();
        boltEntry.resolve();
        return;
      }

      // ── Per-frame live dimensions (resize-resilient) ──────────────────
      ({ symbolWidth, symbolHeight } = getDimensions());
      responsiveScale = symbolHeight / Settings.SYMBOL_HEIGHT;

      // Scalars instead of {x,y} objects — avoids heap allocations per frame per bolt
      const liveStartX = normStart.x * symbolWidth;
      const liveStartY = normStart.y * symbolHeight;
      const liveEndX   = normEnd.x   * symbolWidth;
      const liveEndY   = normEnd.y   * symbolHeight;

      const dx = liveEndX - liveStartX;
      const dy = liveEndY - liveStartY;
      const dist = Math.sqrt(dx * dx + dy * dy);
      if (dist < 1) {
        PIXI.Ticker.shared.remove(boltEntry.tickerFn);
        abortCleanup();
        boltEntry.resolve();
        return;
      }

      const nx = dx / dist;
      const ny = dy / dist;
      const rotation = Math.atan2(dx, -dy);

      const boltMult = boltMultOverride !== null
        ? boltMultOverride
        : (symbolIndex === 0 ? BOLT_CONFIG.TOP_ROW_BOLT_MULT : BOLT_CONFIG.OTHER_ROW_BOLT_MULT);
      const boltMinLength = symbolHeight * boltMult;
      // Cap bolt to (1 - MIN_TRAVEL_FRACTION) of dist so the bolt always has
      // visible travel movement. Without this, short-gap bolts (top row → pot)
      // span the entire distance and sit motionless during Phase 2.
      const maxBoltForTravel = dist * (1 - BOLT_CONFIG.MIN_TRAVEL_FRACTION);
      const boltLength    = Math.min(Math.max(dist * 0.5, boltMinLength), maxBoltForTravel);
      const boltScaleY    = boltLength / lightningNativeHeight;
      const adjDist       = Math.max(0, dist - boltLength);

      // ── Thickness: from the LENGTH when asked for, else from the cell ──
      //
      // Computed here, once per frame, and used by all three phases below so
      // they cannot disagree about how wide the bolt is.
      const scaleX =
        widthFraction !== null
          ? (boltLength * widthFraction) / FXManager.lightningNativeWidth
          : responsiveScale;

      const ballBaseScale = BOLT_CONFIG.BALL_BASE_SCALE * responsiveScale;

      if (elapsed < CHARGE_MS) {
        // ── PHASE 1: CHARGE — energy ball at start + slingshot pullback ──
        if (!chargeStarted && withBalls && FXManager.isReady) {
          chargeStarted = true;
          launchBall = FXManager.emitBall(
            container, { x: liveStartX, y: liveStartY }, { scale: ballBaseScale }
          );
          boltEntry.launchBall = launchBall;
        }

        const ct    = elapsed / CHARGE_MS;
        const eased = 1 - (1 - ct) * (1 - ct); // easeOutQuad

        // Slingshot: pull bolt backwards (away from target) then release
        const pullback = PULLBACK_FRACTION * boltLength * (1 - eased);
        const pullX = liveStartX - nx * pullback;
        const pullY = liveStartY - ny * pullback;

        const growScale = eased * BOLT_CONFIG.CHARGE_GROW_FRACTION;
        wrapper.scale.set(scaleX * BOLT_CONFIG.CHARGE_SCALE_X, Math.max(growScale * boltScaleY, 0.01));
        wrapper.position.set(pullX, pullY);
        wrapper.rotation = rotation;
        wrapper.alpha    = eased * BOLT_CONFIG.CHARGE_ALPHA;

        if (launchBall?.wrapper && !launchBall.wrapper.destroyed) {
          launchBall.wrapper.scale.set(ballBaseScale + eased * (BOLT_CONFIG.BALL_CHARGE_GROW * responsiveScale));
          launchBall.wrapper.position.set(liveStartX, liveStartY);
        }

      } else if (elapsed < CHARGE_MS + duration) {
        // ── PHASE 2: TRAVEL — straight shot start → target ──
        // Gracefully stop the launch ball
        if (launchBall) {
          FXManager.stopTrail(launchBall.emitter, true);
          fadingBalls.push(launchBall);
          launchBall = null;
          boltEntry.launchBall = null;
        }

        const travelT = (elapsed - CHARGE_MS) / duration;
        const eased   = travelT * travelT; // easeInQuad — accelerates into target

        wrapper.scale.set(scaleX, boltScaleY);
        wrapper.alpha = 1;

        // Interpolate along straight line: start → target
        const posT  = eased * adjDist / dist;
        wrapper.position.set(
          liveStartX + dx * posT,
          liveStartY + dy * posT,
        );
        wrapper.rotation = rotation;

      } else if (elapsed < totalDuration) {
        // ── PHASE 3: IMPACT at target ──
        if (!impactStarted && FXManager.isReady) {
          impactStarted = true;
          impactBall = withBalls
            ? FXManager.emitBall(
                container,
                { x: liveEndX, y: liveEndY },
                { scale: ballBaseScale },
              )
            : null;
          boltEntry.impactBall = impactBall;
          // Play lightning end sound at impact (deduped — 1 per frame max)
          _playOncePerFrame(SOUND_NAME.lightning_end);
          onImpact?.();
        }

        const it          = (elapsed - CHARGE_MS - duration) / IMPACT_MS;
        const eased       = 1 - (1 - it) * (1 - it);
        const currentScaleY = boltScaleY * (1 - eased);

        wrapper.scale.set(scaleX, Math.max(currentScaleY, 0.01));
        wrapper.alpha = 1 - eased * BOLT_CONFIG.IMPACT_ALPHA_FADE;

        const boltVisualLength = lightningNativeHeight * currentScaleY;
        wrapper.position.set(
          liveEndX - nx * boltVisualLength,
          liveEndY - ny * boltVisualLength,
        );
        wrapper.rotation = rotation;

        if (impactBall?.wrapper && !impactBall.wrapper.destroyed) {
          impactBall.wrapper.scale.set(ballBaseScale + eased * (BOLT_CONFIG.BALL_CHARGE_GROW * responsiveScale));
          impactBall.wrapper.position.set(liveEndX, liveEndY);
        }

      } else {
        // ── DONE — cleanup ────────────────────────────────────────────────
        PIXI.Ticker.shared.remove(boltEntry.tickerFn);
        boltEntry.tickerFn = null;

        const idx = activeBolts.indexOf(boltEntry);
        if (idx !== -1) activeBolts.splice(idx, 1);

        FXManager.releaseOwnedFx(boltData);
        // Impact/launch balls — ownership-safe release (same pool rules as bolts)
        if (impactBall) {
          fadingBalls.push(impactBall);
          impactBall = null;
        }
        for (const ball of [...fadingBalls, launchBall]) {
          FXManager.releaseOwnedFx(ball);
        }

        boltEntry.resolve();
      }
    };

    activeBolts.push(boltEntry);
    PIXI.Ticker.shared.add(boltEntry.tickerFn);
  });
}
